import _ from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { useApi, useWorkspace } from '~/contexts';
import {
  clientRecordStatuses,
  memberCostMethods,
  memberInvitationStatuses,
  projectHealthRatings,
  projectRecordStatuses,
} from '~/lookups';
import allocationBillableTypes from '~/lookups/allocation-billable-types';
import allocationBookingTypes from '~/lookups/allocation-booking-types';
import approvalStatuses from '~/lookups/approval-statuses';
import clientApprovalStatuses from '~/lookups/client-approval-statuses';
import creditNoteStatuses from '~/lookups/credit-note-statuses';
import employmentTypes from '~/lookups/employment-types';
import invoiceStatuses from '~/lookups/invoice-statuses.js';
import opportunityStageCategories from '~/lookups/opportunity-stage-categories';
import projectBillingTypes from '~/lookups/project-billing-types';
import projectStatuses from '~/lookups/project-statuses';
import projectTaskStatuses from '~/lookups/project-task-statuses';
import timeBillableTypes from '~/lookups/time-billable-types';
import { intervalOptions, QuerySort } from '~/utils';
import { sortIntervals } from '~/utils/intervalOptions';
import useFeatures from './useFeatures';

export default function useSearchParamsConfig() {
  const api = useApi();
  const { workspace } = useWorkspace();
  const features = useFeatures();

  const config = useMemo(
    () => ({
      allocationBillableTypes: {
        multi: true,
        key: 'allocationBillableTypeId',
        aliases: ['allocationBillableType'],
        valid: ['all', ..._.map(allocationBillableTypes, (v) => v.id)],
        serialize: function (value) {
          return !!this.default && !value.length ? 'all' : value?.map((v) => v.id);
        },
        deserialize: (ids) =>
          ids.length === 1 && ids[0] === 'all' ? [] : ids.map((id) => allocationBillableTypes[id]),
      },

      allocationBookingTypeId: {
        key: 'allocationBookingTypeId',
        aliases: ['allocationBookingType'],
        valid: ['all', ..._.map(allocationBookingTypes, (v) => v.id)],
        serialize: function (value) {
          return this.default && !value ? 'all' : value;
        },
        deserialize: (value) => (value === 'all' ? null : value),
      },

      approver: {
        key: 'approverId',
        aliases: ['approver'],
        serialize: (value) => value?.id ?? 'all',
        deserialize: (ids) =>
          ids === 'all'
            ? null
            : api.www
                .workspaces(workspace.id)
                .members()
                .options({ ids })
                .then(({ data }) => data[0]),
      },

      client: {
        key: 'clientId',
        aliases: ['client'],
        serialize: (value) => value?.id,
        deserialize: (ids) =>
          ids === 'all'
            ? null
            : api.www
                .workspaces(workspace.id)
                .clients()
                .options({ ids })
                .then(({ data }) => data[0]),
      },

      clients: {
        key: 'clientId',
        aliases: ['client'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .clients()
            .options({ ids })
            .then(({ data }) => data),
      },

      clientBusinessUnits: {
        key: 'clientBusinessUnitId',
        aliases: ['clientBusinessUnit'],
        multi: true,
        valid: !features.businessUnits ? [] : undefined,
        serialize: function (value) {
          return !!this.default && value.length === 0 ? ['all'] : value?.map((v) => v.id);
        },
        deserialize: (ids) =>
          ids.length === 1 && ids[0] === 'all'
            ? []
            : api.www
                .workspaces(workspace.id)
                .businessUnits()
                .get({ ids })
                .then(({ data }) => data),
      },

      clientIndustries: {
        key: 'clientIndustryId',
        aliases: ['clientIndustry'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .industries()
            .get({ ids })
            .then(({ data }) => data),
      },

      clientLocations: {
        key: 'clientLocationId',
        aliases: ['clientLocation'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .locations()
            .get({ ids })
            .then(({ data }) => data),
      },

      clientOwners: {
        key: 'clientOwnerId',
        aliases: ['clientOwner'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .members()
            .options({ ids })
            .then(({ data }) => data),
      },

      clientPractices: {
        key: 'clientPracticeId',
        aliases: ['clientPractice'],
        multi: true,
        valid: !features.practices ? [] : undefined,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .practices()
            .get({ ids })
            .then(({ data }) => data),
      },

      clientRecordStatusId: {
        key: 'clientRecordStatusId',
        valid: ['all', ..._.map(clientRecordStatuses, (v) => v.id)],
        serialize: function (value) {
          return this.default && !value ? 'all' : value;
        },
        deserialize: (value) => (value === 'all' ? null : value),
      },

      clientSalesRepresentatives: {
        key: 'clientSalesRepresentativeId',
        aliases: ['clientSalesRepresentative'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .members()
            .options({ ids })
            .then(({ data }) => data),
      },

      clientTags: {
        key: 'clientTagId',
        aliases: ['clientTag'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .clientTags()
            .get({ ids })
            .then(({ data }) => data),
      },

      contactTags: {
        key: 'contactTagId',
        aliases: ['contactTag'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .contactTags()
            .get({ ids })
            .then(({ data }) => data),
      },

      companies: {
        key: 'companyId',
        aliases: ['company'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .companies()
            .get({ ids })
            .then(({ data }) => data),
      },

      companyIsActive: {
        valid: ['all', 'true', 'false'],
        serialize: function (value) {
          return this.default && !value ? 'all' : value;
        },
        deserialize: (value) => (value === 'all' ? null : value),
      },

      companyOwners: {
        key: 'companyOwnerId',
        aliases: ['companyOwner'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .members()
            .options({ ids })
            .then(({ data }) => data),
      },

      companyPractices: {
        key: 'companyPracticeId',
        aliases: ['companyPractice'],
        multi: true,
        valid: !features.practices ? [] : undefined,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .practices()
            .get({ ids })
            .then(({ data }) => data),
      },

      companySpendTiers: {
        key: 'companySpendTierId',
        aliases: ['companySpendTiers'],
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .companySpendTiers()
            .get({ ids })
            .then(({ data }) => data),
      },

      companyTypes: {
        key: 'companyTypeId',
        aliases: ['companyTypes'],
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .companyTypes()
            .get({ ids })
            .then(({ data }) => data),
      },

      companyIndustries: {
        key: 'companyIndustryId',
        aliases: ['companyIndustry'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .industries()
            .get({ ids })
            .then(({ data }) => data),
      },

      contactOwners: {
        key: 'contactOwnerId',
        aliases: ['contactOwner'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .members()
            .options({ ids })
            .then(({ data }) => data),
      },

      creditNoteSent: {
        valid: ['all', 'yes', 'no'],
        serialize: function (value) {
          return this.default && !value ? 'all' : value;
        },
        deserialize: (value) => (value === 'all' ? null : value),
      },

      creditNoteStatuses: {
        key: 'creditNoteStatusId',
        aliases: ['creditNoteStatus'],
        multi: true,
        valid: ['all', ..._.map(creditNoteStatuses, (v) => v.id)],
        serialize: function (value) {
          return !!this.default && value.length === 0 ? ['all'] : value?.map((v) => v.id);
        },
        deserialize: (ids) => (ids.length === 1 && ids[0] === 'all' ? [] : ids.map((id) => creditNoteStatuses[id])),
      },

      currency: {
        key: 'currency',
        default: workspace.currency,
        deserialize: (code) =>
          api
            .currencies()
            .get({ code })
            .then(({ data }) => data[0]?.code),
      },

      expenseApprovalStatuses: {
        key: 'expenseApprovalStatusId',
        aliases: ['status', 'expenseStatus'],
        multi: true,
        valid: _.map(approvalStatuses, (v) => v.id),
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) => ids.map((id) => approvalStatuses[id]),
      },

      expenseBillableTypeId: {
        key: 'expenseBillableTypeId',
        aliases: ['expenseBillableType', 'billableType'],
        valid: ['billable', 'non_billable'],
      },

      expenseClientApprovalStatuses: {
        key: 'expenseClientApprovalStatusId',
        aliases: ['clientStatus', 'expenseClientStatus'],
        multi: true,
        valid: features.clientApprovals ? _.map(clientApprovalStatuses, (v) => v.id) : [],
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) => ids.map((id) => clientApprovalStatuses[id]),
      },

      expenseCategories: {
        key: 'expenseCategoryId',
        aliases: ['expenseCategory'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .expenseCategories()
            .get({ ids })
            .then(({ data }) => data),
      },

      expenseInvoiced: { key: 'expenseInvoiced', valid: ['yes', 'no'] },

      expenseIsReimbursed: {
        key: 'expenseIsReimbursed',
        aliases: ['isReimbursed'],
        valid: ['yes', 'no'],
      },

      expenseTypeId: {
        key: 'expenseTypeId',
        valid: ['member', 'project'],
      },

      expenseReimbursableTypeId: {
        key: 'expenseReimbursableTypeId',
        aliases: ['reimbursableType'],
        valid: ['reimbursable', 'non_reimbursable'],
      },

      invoiced: { key: 'invoiced', valid: ['yes', 'no'] },

      invoiceSent: {
        valid: ['all', 'yes', 'no'],
        serialize: function (value) {
          return this.default && !value ? 'all' : value;
        },
        deserialize: (value) => (value === 'all' ? null : value),
      },

      invoiceStatuses: {
        key: 'invoiceStatusId',
        aliases: ['invoiceStatus'],
        multi: true,
        valid: ['all', ..._.map(invoiceStatuses, (v) => v.id)],
        serialize: function (value) {
          return !!this.default && value.length === 0 ? ['all'] : value?.map((v) => v.id);
        },
        deserialize: (ids) => (ids.length === 1 && ids[0] === 'all' ? [] : ids.map((id) => invoiceStatuses[id])),
      },

      invoiceItems: {
        key: 'invoiceItemId',
        aliases: ['invoiceItem'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .invoiceItems()
            .get({ ids })
            .then(({ data }) => data),
      },

      members: {
        key: 'memberId',
        aliases: ['member'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .members()
            .options({ ids })
            .then(({ data }) => data),
      },

      memberBillableTypeId: {
        key: 'memberBillableTypeId',
        aliases: ['memberBillableType'],
        valid: ['all', 'billable', 'non_billable'],
        serialize: function (value) {
          return this.default && !value ? 'all' : value;
        },
        deserialize: (value) => (value === 'all' ? null : value),
      },

      memberCostMethods: {
        key: 'memberCostMethodId',
        aliases: ['memberCostMethod'],
        multi: true,
        valid: ['all', ..._.map(memberCostMethods, (v) => v.id)],
        serialize: function (value) {
          return !!this.default && !value.length ? 'all' : value?.map((v) => v.id);
        },
        deserialize: (ids) => (ids.length === 1 && ids[0] === 'all' ? [] : ids.map((id) => memberCostMethods[id])),
      },

      memberDisciplines: {
        key: 'memberDisciplineId',
        aliases: ['memberDiscipline'],
        multi: true,
        valid: !features.disciplines ? [] : undefined,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .disciplines()
            .get({ ids })
            .then(({ data }) => data),
      },

      memberEmploymentTypes: {
        key: 'memberEmploymentTypeId',
        aliases: ['employmentType'],
        multi: true,
        valid: ['all', ..._.map(employmentTypes, (v) => v.id)],
        serialize: function (value) {
          return !!this.default && value.length === 0 ? ['all'] : value?.map((v) => v.id);
        },
        deserialize: (ids) => (ids.length === 1 && ids[0] === 'all' ? [] : ids.map((id) => employmentTypes[id])),
      },

      memberInvitationStatuses: {
        key: 'memberInvitationStatusId',
        aliases: ['memberInvitationStatus'],
        multi: true,
        valid: _.map(memberInvitationStatuses, (v) => v.id),
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) => ids.map((id) => memberInvitationStatuses[id]),
      },

      memberBusinessUnits: {
        key: 'memberBusinessUnitId',
        aliases: ['memberBusinessUnit'],
        multi: true,
        valid: !features.businessUnits ? [] : undefined,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .businessUnits()
            .get({ ids })
            .then(({ data }) => data),
      },

      memberJobTitles: {
        key: 'memberJobTitleId',
        aliases: ['jobTitle'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .jobTitles()
            .get({ ids })
            .then(({ data }) => data),
      },

      memberLevels: {
        key: 'memberLevelId',
        aliases: ['memberLevel'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .memberLevels()
            .get({ ids })
            .then(({ data }) => data),
      },

      memberLocations: {
        key: 'memberLocationId',
        aliases: ['memberLocation'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .locations()
            .get({ ids })
            .then(({ data }) => data),
      },

      memberManagers: {
        key: 'memberManagerId',
        aliases: ['manager'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .members()
            .options({ ids })
            .then(({ data }) => data),
      },

      memberPractices: {
        key: 'memberPracticeId',
        aliases: ['memberPractice'],
        multi: true,
        valid: !features.practices ? [] : undefined,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .practices()
            .get({ ids })
            .then(({ data }) => data),
      },

      memberSecurityRoles: {
        key: 'memberSecurityRoleId',
        aliases: ['securityRole'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .securityRoles()
            .get({ ids })
            .then(({ data }) => data),
      },

      memberStatusId: {
        key: 'memberStatusId',
        aliases: ['memberStatus'],
        valid: ['all', 'active', 'inactive'],
        serialize: function (value) {
          return this.default && !value ? 'all' : value;
        },
        deserialize: (value) => (value === 'all' ? null : value),
      },

      memberSkills: {
        key: 'memberSkillId',
        aliases: ['memberSkill'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .skills()
            .get({ ids })
            .then(({ data }) => data),
      },

      memberCertifications: {
        key: 'memberCertificationId',
        aliases: ['memberCertification'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .memberCertifications()
            .get({ ids })
            .then(({ data }) => data),
      },

      memberTags: {
        key: 'memberTagId',
        aliases: ['memberTag'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .memberTags()
            .get({ ids })
            .then(({ data }) => data),
      },

      opportunities: {
        key: 'opportunityId',
        aliases: ['opportunity'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .opportunities()
            .get({ ids })
            .then(({ data }) => data),
      },

      opportunityOwners: {
        key: 'opportunityOwnerId',
        aliases: ['opportunityOwner'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .members()
            .options({ ids })
            .then(({ data }) => data),
      },

      opportunityStages: {
        key: 'opportunityStageId',
        aliases: ['opportunityStage'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) => {
          return api.www
            .workspaces(workspace.id)
            .opportunityStages()
            .get({ ids })
            .then(({ data }) => data);
        },
      },

      opportunityStageCategories: {
        key: 'opportunityStageCategoryId',
        multi: true,
        default: [opportunityStageCategories.open],
        valid: ['all', ..._.map(opportunityStageCategories, (v) => v.id)],
        serialize: function (value) {
          return !!this.default && value.length === 0 ? ['all'] : value?.map((v) => v.id);
        },
        deserialize: (ids) => {
          return ids.length === 1 && ids[0] === 'all' ? [] : ids.map((id) => opportunityStageCategories[id]);
        },
      },

      opportunityTags: {
        key: 'opportunityTagId',
        aliases: ['opportunityTag'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .opportunityTags()
            .get({ ids })
            .then(({ data }) => data),
      },

      opportunityTypes: {
        key: 'opportunityTypeId',
        aliases: ['opportunityType'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .opportunityTypes()
            .get({ ids })
            .then(({ data }) => data),
      },

      opportunityPipelines: {
        key: 'opportunityPipelineId',
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .opportunityPipelines()
            .get({ ids })
            .then(({ data }) => data),
      },

      opportunitySalesforceSynced: { key: 'opportunitySalesforceSynced', valid: ['yes', 'no'] },

      opportunitySalesforceRecordTypes: {
        key: 'opportunitySalesforceRecordType',
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .opportunities()
            .getSalesforceRecordTypes({ ids })
            .then(({ data }) => data),
      },

      opportunityHubspotSynced: { key: 'opportunityHubspotSynced', valid: ['yes', 'no'] },

      invoiceAccountingIntegrations: {
        key: 'invoiceAccountingConnectionId',
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .integrations()
            .accounting.options({ ids })
            .then(({ data }) => data),
      },

      creditNoteAccountingIntegrations: {
        key: 'creditNoteAccountingConnectionId',
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .integrations()
            .accounting.options({ ids })
            .then(({ data }) => data),
      },

      paymentAccountingIntegrations: {
        key: 'paymentAccountingConnectionId',
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .integrations()
            .accounting.options({ ids })
            .then(({ data }) => data),
      },

      paymentMethods: {
        key: 'paymentMethodId',
        aliases: ['paymentMethods'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .paymentMethods()
            .get({ ids })
            .then(({ data }) => data),
      },

      period: {
        keys: ['period', 'start', 'end'],
        intervals: intervalOptions,
        serialize: function (value) {
          const [periodKey, startKey, endKey] = this.keys;
          return { [periodKey]: value.key, [startKey]: value.start ?? 'not_set', [endKey]: value.end ?? 'not_set' };
        },
        deserialize: function (value, urlSearchParams) {
          const [periodKey, startKey, endKey] = this.keys;
          const dates = urlSearchParams.get('dates');
          const period = urlSearchParams.get(periodKey);

          let option;
          if (dates !== 'fixed' && period && period !== 'custom') {
            option = _.find(sortIntervals(this.intervals), (i) => i.key === period);
          }

          if (option) {
            return { start: option.start, end: option.end, key: option.key, unit: option.unit };
          } else {
            urlSearchParams.delete(periodKey);

            let start = urlSearchParams.get(startKey);
            if (!moment(start, true).isValid()) {
              if (start !== 'not_set') urlSearchParams.delete(startKey);
              start = null;
            }

            let end = urlSearchParams.get(endKey);
            if (!moment(end, true).isValid()) {
              if (end !== 'not_set') urlSearchParams.delete(endKey);
              end = null;
            }

            option =
              _.find(sortIntervals(this.intervals), (i) => i.start === start && i.end === end) ??
              intervalOptions.custom;

            return { start, end, key: option.key, unit: option.unit };
          }
        },
      },

      periodByUnit: {
        keys: ['period', 'start', 'end', 'unit'],
        intervals: () => intervalOptions,
        units: ['day', 'week', 'month'],
        serialize: function (value) {
          const [periodKey, startKey, endKey] = this.keys;
          return { [periodKey]: value.key, [startKey]: value.start ?? 'not_set', [endKey]: value.end ?? 'not_set' };
        },
        deserialize: function (value, urlSearchParams) {
          const [periodKey, startKey, endKey, unitKey] = this.keys;
          let unit = this.units.find((u) => u === urlSearchParams.get(unitKey)) ?? 'month';

          const intervals = this.intervals(unit);

          const dates = urlSearchParams.get('dates');
          const period = urlSearchParams.get(periodKey);
          let option;
          if (dates !== 'fixed' && period && period !== 'custom') {
            option = _.find(intervals, (i) => i.key === period);
          }

          if (option) {
            return { start: option.start, end: option.end, key: option.key, unit: option.unit };
          } else {
            urlSearchParams.delete(periodKey);

            let start = urlSearchParams.get(startKey);
            if (!moment(start, true).isValid()) {
              if (start !== 'not_set') urlSearchParams.delete(startKey);
              start = null;
            }

            let end = urlSearchParams.get(endKey);
            if (!moment(end, true).isValid()) {
              if (end !== 'not_set') urlSearchParams.delete(endKey);
              end = null;
            }

            if (!start || !end) {
              option = this.defaults[unit];
            } else {
              option =
                _.find(sortIntervals(intervals), (i) => i.unit === unit && i.start === start && i.end === end) ??
                intervalOptions.custom;
            }

            return { start: start ?? option.start, end: end ?? option.end, key: option.key, unit: unit };
          }
        },
      },

      opportunityAmountGreaterThanOrEqual: {
        key: 'opportunityAmountGreaterThanOrEqual',
        serialize: function (value) {
          return Number(value) ? value : 0;
        },
        deserialize: (value) => {
          return Number(value) && !_.isNaN(value) ? Number(value) : 0;
        },
      },

      opportunityPractices: {
        key: 'opportunityPracticeId',
        aliases: ['opportunityPractice'],
        multi: true,
        valid: !features.practices ? [] : undefined,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .practices()
            .get({ ids })
            .then(({ data }) => data),
      },

      placeholderDisciplines: {
        key: 'placeholderDisciplineId',
        multi: true,
        valid: !features.disciplines ? [] : undefined,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .disciplines()
            .get({ ids })
            .then(({ data }) => data),
      },

      placeholderLocations: {
        key: 'placeholderLocationId',
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .locations()
            .get({ ids })
            .then(({ data }) => data),
      },

      placeholderPractices: {
        key: 'placeholderPracticeId',
        multi: true,
        valid: !features.practices ? [] : undefined,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .practices()
            .get({ ids })
            .then(({ data }) => data),
      },

      placeholderSkills: {
        key: 'placeholderSkillId',
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .skills()
            .get({ ids })
            .then(({ data }) => data),
      },

      placeholderStatusId: {
        key: 'placeholderStatusId',
        valid: ['all', 'active', 'inactive'],
        serialize: function (value) {
          return this.default && !value ? 'all' : value;
        },
        deserialize: (value) => (value === 'all' ? null : value),
      },

      projectPractices: {
        key: 'projectPracticeId',
        aliases: ['projectPractice'],
        multi: true,
        valid: !features.practices ? [] : undefined,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .practices()
            .get({ ids })
            .then(({ data }) => data),
      },

      project: {
        key: 'projectId',
        aliases: ['project'],
        serialize: (value) => value?.id,
        deserialize: (ids) =>
          ids === 'all'
            ? null
            : api.www
                .workspaces(workspace.id)
                .projects()
                .selectOptions({ ids, activeOnly: false })
                .then(({ data }) => data[0]),
      },

      projects: {
        key: 'projectId',
        aliases: ['project'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .projects()
            .selectOptions({ ids, activeOnly: false })
            .then(({ data }) => data),
      },

      projectAdmins: {
        key: 'projectAdminId',
        aliases: ['projectAdmin', 'administrators'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .members()
            .options({ ids })
            .then(({ data }) => data),
      },

      projectBillingTypes: {
        key: 'projectBillingTypeId',
        aliases: ['projectBillingType'],
        multi: true,
        valid: ['all', ..._.map(projectBillingTypes, (v) => v.id)],
        serialize: function (value) {
          return !!this.default && value.length === 0 ? ['all'] : value?.map((v) => v.id);
        },
        deserialize: (ids) => (ids.length === 1 && ids[0] === 'all' ? [] : ids.map((id) => projectBillingTypes[id])),
      },

      projectBusinessUnits: {
        key: 'projectBusinessUnitId',
        aliases: ['projectBusinessUnit'],
        multi: true,
        valid: !features.businessUnits ? [] : undefined,
        serialize: function (value) {
          return !!this.default && value.length === 0 ? ['all'] : value?.map((v) => v.id);
        },
        deserialize: (ids) =>
          ids.length === 1 && ids[0] === 'all'
            ? []
            : api.www
                .workspaces(workspace.id)
                .businessUnits()
                .get({ ids })
                .then(({ data }) => data),
      },

      projectGroups: {
        key: 'projectGroupId',
        aliases: ['projectGroup'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .projectGroups()
            .get({ ids })
            .then(({ data }) => data),
      },

      projectRecordStatusId: {
        key: 'projectRecordStatusId',
        aliases: ['recordStatusId'],
        valid: ['all', ..._.map(projectRecordStatuses, (v) => v.id)],
        serialize: function (value) {
          return this.default && !value ? 'all' : value;
        },
        deserialize: (value) => (value === 'all' ? null : value),
      },

      projectSalesRepresentatives: {
        key: 'projectSalesRepresentativeId',
        aliases: ['projectSalesRepresentative'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .members()
            .options({ ids })
            .then(({ data }) => data),
      },

      projectStatuses: {
        key: 'projectStatusId',
        aliases: ['projectStatus'],
        multi: true,
        valid: ['all', ..._.map(projectStatuses, (v) => v.id)],
        serialize: function (value) {
          return !!this.default && value.length === 0 ? ['all'] : value?.map((v) => v.id);
        },
        deserialize: (ids) => (ids.length === 1 && ids[0] === 'all' ? [] : ids.map((id) => projectStatuses[id])),
      },

      projectTags: {
        key: 'projectTagId',
        aliases: ['projectTag'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .projectTags()
            .get({ ids })
            .then(({ data }) => data),
      },

      projectTypes: {
        key: 'projectTypeId',
        aliases: ['projectType'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .projectTypes()
            .get({ ids })
            .then(({ data }) => data),
      },

      projectTeamStatusId: {
        key: 'projectTeamStatusId',
        aliases: ['projectTeamStatus'],
        valid: ['all', 'active', 'inactive'],
        serialize: function (value) {
          return this.default && !value ? 'all' : value;
        },
        deserialize: (value) => (value === 'all' ? null : value),
      },

      projectBudgetHealth: {
        multi: true,
        key: 'projectBudgetHealthId',
        aliases: ['projectBudgetHealth'],
        valid: features.projectHealth ? _.map(projectHealthRatings, (v) => v.id) : [],
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) => ids.map((id) => projectHealthRatings[id]),
      },

      projectScheduleHealth: {
        key: 'projectScheduleHealthId',
        aliases: ['projectScheduleHealth'],
        multi: true,
        valid: features.projectHealth ? _.map(projectHealthRatings, (v) => v.id) : [],
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) => ids.map((id) => projectHealthRatings[id]),
      },

      projectClientSatisfaction: {
        key: 'projectClientSatisfactionId',
        aliases: ['projectClientSatisfaction'],
        multi: true,
        valid: features.projectHealth ? _.map(projectHealthRatings, (v) => v.id) : [],
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) => ids.map((id) => projectHealthRatings[id]),
      },

      projectTeamSatisfaction: {
        key: 'projectTeamSatisfactionId',
        aliases: ['projectTeamSatisfaction'],
        multi: true,
        valid: features.projectHealth ? _.map(projectHealthRatings, (v) => v.id) : [],
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) => ids.map((id) => projectHealthRatings[id]),
      },

      projectRoles: {
        key: 'projectRoleId',
        aliases: ['projectRole'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids, urlSearchParams) => {
          const projectId = urlSearchParams.get('projectId') ?? urlSearchParams.get('project');

          if (!projectId) {
            urlSearchParams.delete('projectRoleId');
            urlSearchParams.delete('projectRole');
            return null;
          }

          return api.www
            .workspaces(workspace.id)
            .projects(projectId)
            .roles()
            .get({ ids })
            .then(({ data }) => data);
        },
      },

      projectRoleDisciplines: {
        key: 'projectRoleDisciplineId',
        aliases: ['roleDiscipline'],
        multi: true,
        valid: !features.disciplines ? [] : undefined,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .disciplines()
            .get({ ids })
            .then(({ data }) => data),
      },

      projectRoleStatusId: {
        key: 'projectRoleStatusId',
        aliases: ['roleStatus'],
        valid: ['all', 'active', 'inactive'],
        serialize: function (value) {
          return this.default && !value ? 'all' : value;
        },
        deserialize: (value) => (value === 'all' ? null : value),
      },

      projectTasks: {
        key: 'projectTaskId',
        aliases: ['projectTask'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids, urlSearchParams) => {
          const projectId = urlSearchParams.get('projectId') ?? urlSearchParams.get('project');

          if (!projectId) {
            urlSearchParams.delete('projectTaskId');
            urlSearchParams.delete('projectTask');
            return null;
          }

          return api.www
            .workspaces(workspace.id)
            .projects(projectId)
            .tasks()
            .options({ projectTaskId: ids })
            .then(({ data }) => data);
        },
      },

      projectTaskStatuses: {
        key: 'projectTaskStatusId',
        aliases: ['projectTaskStatus'],
        multi: true,
        valid: ['all', ..._.map(projectTaskStatuses, (v) => v.id)],
        serialize: function (value) {
          return !!this.default && value.length === 0 ? ['all'] : value?.map((v) => v.id);
        },
        deserialize: (ids) => (ids.length === 1 && ids[0] === 'all' ? [] : ids.map((id) => projectTaskStatuses[id])),
      },

      projectTaskRecordStatusId: {
        key: 'projectTaskRecordStatusId',
        valid: ['all', 'active', 'archived'],
        serialize: function (value) {
          return this.default && !value ? 'all' : value;
        },
        deserialize: (value) => (value === 'all' ? null : value),
      },

      projectTaskTags: {
        key: 'projectTaskTagId',
        aliases: ['projectTaskTag'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .projectTaskTags()
            .get({ ids })
            .then(({ data }) => data),
      },

      resourceDisciplines: {
        key: 'resourceDisciplineId',
        aliases: ['resourceDiscipline', 'resourceDisciplines', 'discipline'],
        multi: true,
        valid: !features.disciplines ? [] : undefined,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .disciplines()
            .get({ ids })
            .then(({ data }) => data),
      },

      resourceLocations: {
        key: 'resourceLocationId',
        aliases: ['resourceLocation', 'resourceLocations'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .locations()
            .get({ ids })
            .then(({ data }) => data),
      },

      resourcePractices: {
        key: 'resourcePracticeId',
        aliases: ['resourcePractice', 'resourcePractices'],
        multi: true,
        valid: !features.practices ? [] : undefined,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .practices()
            .get({ ids })
            .then(({ data }) => data),
      },

      resourceSkills: {
        key: 'resourceSkillId',
        aliases: ['resourceSkill', 'resourceSkills'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) =>
          api.www
            .workspaces(workspace.id)
            .skills()
            .get({ ids })
            .then(({ data }) => data),
      },

      resourceStatusId: {
        key: 'resourceStatusId',
        aliases: ['resourceStatus'],
        valid: ['all', 'active', 'inactive'],
        serialize: function (value) {
          return this.default && !value ? 'all' : value;
        },
        deserialize: (value) => (value === 'all' ? null : value),
      },

      resourceTypeId: {
        key: 'resourceTypeId',
        valid: ['member', 'placeholder'],
      },

      sort: {
        key: 'sort',
        deserialize: (value) => {
          const parts = value.split(':');
          const column = parts.slice(0, parts.length - 1).join(':');
          const direction = parts[parts.length - 1];

          return new QuerySort(column, direction);
        },
      },

      timeApprovalStatuses: {
        key: 'timeApprovalStatusId',
        aliases: ['status', 'timeStatus'],
        multi: true,
        valid: _.map(approvalStatuses, (v) => v.id),
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) => ids.map((id) => approvalStatuses[id]),
      },

      timeBillableTypes: {
        key: 'timeBillableTypeId',
        aliases: ['billableType'],
        multi: true,
        valid: ['all', ..._.map(timeBillableTypes, (v) => v.id)],
        serialize: function (value) {
          return !!this.default && value.length === 0 ? ['all'] : value?.map((v) => v.id);
        },
        deserialize: (ids) => (ids.length === 1 && ids[0] === 'all' ? [] : ids.map((id) => timeBillableTypes[id])),
      },

      timeClientApprovalStatuses: {
        key: 'timeClientApprovalStatusId',
        aliases: ['clientStatus', 'timeClientStatus'],
        multi: true,
        valid: features.clientApprovals ? _.map(clientApprovalStatuses, (v) => v.id) : [],
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) => ids.map((id) => clientApprovalStatuses[id]),
      },

      timeInvoiced: { key: 'timeInvoiced', valid: ['yes', 'no'] },

      timeOffTypes: {
        key: 'timeOffTypeId',
        aliases: ['timeOffType'],
        multi: true,
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) => {
          return api.www
            .workspaces(workspace.id)
            .timeOffTypes()
            .get({ ids })
            .then(({ data }) => data);
        },
      },

      varianceBillableTypes: {
        key: 'varianceBillableTypeId',
        aliases: ['varianceBillableType'],
        multi: true,
        valid: _.map(timeBillableTypes, (v) => v.id),
        serialize: (value) => value?.map((v) => v.id),
        deserialize: (ids) => ids.map((id) => timeBillableTypes[id]),
      },
    }),
    [api, workspace.id, features, workspace.currency],
  );

  return config;
}
